import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { IMAGES } from '../assets/constant';
import { bannerApi, useGetBannersQuery } from '../redux/bannerApi';
import { employeeApi } from '../redux/employeeApi';
import { getBannerSlides, preloadBannerSlides } from '../utils/bannerSlides';
import BannerSlider from './BannerSlider';

function LoginPage() {
  const [employeeCode, setEmployeeCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [firstBannerReady, setFirstBannerReady] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { login, isAuthenticated } = useAuth();
  const { data: bannersRes } = useGetBannersQuery();

  const loginSlides = React.useMemo(
    () => getBannerSlides(bannersRes, 'login', { size: 'large' }),
    [bannersRes]
  );

  useEffect(() => {
    if (!loginSlides.length) return;
    preloadBannerSlides([loginSlides[0]]);
  }, [loginSlides]);

  useEffect(() => {
    if (!loginSlides.length || !firstBannerReady) return undefined;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % loginSlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [loginSlides.length, firstBannerReady]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/landing', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    setCurrentSlide((prev) =>
      loginSlides.length ? prev % loginSlides.length : 0
    );
  }, [loginSlides.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employeeCode.trim() || !password.trim()) {
      toast.error('Please enter employee code and password');
      return;
    }
    try {
      const user = await login(employeeCode.trim(), password);
      toast.success('Logged in successfully.');
      dispatch(employeeApi.util.prefetch('getActiveDepartments', undefined));
      dispatch(bannerApi.util.prefetch('getBanners', undefined));
      if (
        String(user?.roleKey || '').toLowerCase() === 'backend' ||
        String(user?.area || '').toLowerCase() === 'backend'
      ) {
        navigate('/dashboard/backend', { replace: true });
      } else {
        navigate('/landing', { replace: true });
      }
    } catch (err) {
      const apiMessage =
        err?.data?.message ||
        err?.message ||
        'Login failed. Please check your credentials.';
      toast.error(apiMessage);
    }
  };

  return (
    <div className="flex min-h-screen items-start justify-center overflow-y-auto bg-[#F7E5D5] px-4 py-4 sm:px-6 md:items-center md:py-8">
      <div className="my-auto flex w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl md:min-h-[36rem] md:flex-row">
        {/* Form */}
        <div className="flex flex-1 items-center justify-center px-4 py-8 sm:px-8 md:px-10 md:py-12 lg:py-16">
          <div className="w-full max-w-md">
            <Link to="/" className="mb-4 flex items-center justify-center md:justify-start">
              <img
                src={IMAGES.LOGO}
                alt="Grover Sweets Logo"
                className="h-16 w-auto object-contain sm:h-20 lg:h-24"
              />
            </Link>
            <h1 className="mb-1 text-center text-2xl font-bold text-gray-900 md:text-left">
              Login
            </h1>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5 sm:gap-6">
              <div className="space-y-2">
                <label htmlFor="employeeCode" className="block text-xs font-medium text-gray-800">
                  Employee Code
                </label>
                <input
                  id="employeeCode"
                  type="text"
                  placeholder="Enter your employee code"
                  value={employeeCode}
                  onChange={(e) => setEmployeeCode(e.target.value)}
                  autoComplete="username"
                  className="w-full rounded-lg border border-[#e0dbd4] bg-[#F7E5D5] px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="block text-xs font-medium text-gray-800">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="w-full rounded-lg border border-[#e0dbd4] bg-[#F7E5D5] px-3 py-2.5 pr-10 text-sm text-gray-900 placeholder:text-gray-400 focus:border-brand focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-gray-500 hover:text-brand"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {showPassword ? (
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </>
                      ) : (
                        <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
              </div>
              <button
                type="submit"
                className="mt-1 inline-flex w-full items-center justify-center rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-white shadow-sm"
              >
                Sign in
              </button>
            </form>
          </div>
        </div>

        {/* Banner — desktop right column; uses `login_page` images.large from /api/banners */}
        <div className="hidden md:relative md:flex md:min-h-[320px] md:flex-1 md:p-6 lg:p-8">
          <div className="h-full w-full">
            <BannerSlider
              key="login-banner"
              scope="login"
              slides={loginSlides}
              currentSlide={currentSlide}
              onSelectSlide={setCurrentSlide}
              onFirstSlideReady={setFirstBannerReady}
              variant="login"
              alt="Login banner"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
